
import { useTranslation } from "react-i18next";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ExternalRoundStats } from "@/hooks/useCourseStats";
import { format } from "date-fns";
import { fr, enUS, es } from "date-fns/locale";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ReferenceLine,
} from "recharts";

interface ScoreEvolutionChartProps {
  stats: ExternalRoundStats[];
}

const ScoreEvolutionChart = ({ stats }: ScoreEvolutionChartProps) => {
  const { t, i18n } = useTranslation('course-stats');

  const getLocale = () => {
    switch (i18n.language) {
      case 'fr': return fr;
      case 'es': return es;
      default: return enUS;
    }
  };

  // Sort by date ascending for chart
  const sortedStats = [...stats].sort(
    (a, b) => new Date(a.round_date).getTime() - new Date(b.round_date).getTime()
  );

  const chartData = sortedStats.map((stat) => ({
    date: format(new Date(stat.round_date), "dd/MM", { locale: getLocale() }),
    fullDate: format(new Date(stat.round_date), "PPP", { locale: getLocale() }),
    scoreVsPar: stat.score_vs_par,
    puttsPerHole: stat.putts_per_hole,
    course: stat.course_name,
  }));

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-3 border rounded-lg shadow-lg">
          <p className="font-semibold">{payload[0]?.payload?.fullDate}</p>
          <p className="text-sm text-muted-foreground">{payload[0]?.payload?.course}</p>
          <div className="mt-2 space-y-1">
            {payload.map((entry: any, index: number) => (
              <p key={index} style={{ color: entry.color }} className="text-sm">
                {entry.name}: {entry.value >= 0 && entry.dataKey === "scoreVsPar" ? "+" : ""}
                {entry.value?.toFixed(entry.dataKey === "puttsPerHole" ? 2 : 0)}
              </p>
            ))}
          </div>
        </div>
      );
    }
    return null;
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('charts.scoreEvolution')}</CardTitle>
      </CardHeader>
      <CardContent>
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={chartData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
            <XAxis dataKey="date" tick={{ fontSize: 12 }} />
            <YAxis 
              yAxisId="left" 
              tick={{ fontSize: 12 }}
              label={{ value: t('charts.scoreVsPar'), angle: -90, position: 'insideLeft', fontSize: 12 }}
            />
            <YAxis 
              yAxisId="right" 
              orientation="right"
              tick={{ fontSize: 12 }}
              label={{ value: t('charts.puttsPerHole'), angle: 90, position: 'insideRight', fontSize: 12 }}
              domain={[1, 3]}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend />
            <ReferenceLine yAxisId="left" y={0} stroke="#888" strokeDasharray="5 5" />
            <Line
              yAxisId="left"
              type="monotone"
              dataKey="scoreVsPar"
              name={t('charts.scoreVsPar')}
              stroke="#059669"
              strokeWidth={2}
              dot={{ r: 4 }}
              activeDot={{ r: 6 }}
            />
            <Line
              yAxisId="right"
              type="monotone"
              dataKey="puttsPerHole"
              name={t('charts.puttsPerHole')}
              stroke="#7c3aed"
              strokeWidth={2}
              dot={{ r: 4 }}
              activeDot={{ r: 6 }}
            />
          </LineChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
};

export default ScoreEvolutionChart;
